import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import EmployerLayout from '../../layouts/EmployerLayout';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Input from '../../components/common/Input';
import SecuritySection from '../../components/common/SecuritySection';
import toast from 'react-hot-toast';

export default function EmployerProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    api.get('/employer/profile')
      .then((r) => { setProfile(r.data.data); setForm(r.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  async function handleSave() {
    setSaving(true);
    try {
      await api.put('/employer/profile', {
        company_name:   form.company_name,
        contact_person: form.contact_person,
        designation:    form.designation,
        website:        form.website,
        address_line:   form.address_line,
        pincode:        form.pincode,
        description:    form.description,
      });
      setProfile((p) => ({ ...p, ...form }));
      setEditing(false);
      toast.success('Profile updated');
    } catch {}
    finally { setSaving(false); }
  }

  if (loading) {
    return (
      <EmployerLayout>
        <div className="flex justify-center py-16">
          <div className="animate-spin h-8 w-8 border-4 border-india border-t-transparent rounded-full" />
        </div>
      </EmployerLayout>
    );
  }

  if (!profile) {
    return (
      <EmployerLayout>
        <div className="text-center py-16">
          <span className="text-4xl">🏢</span>
          <p className="text-gray-500 mt-3 mb-4">No employer profile found</p>
          <Link to="/employer/profile/create"><Button>Create Profile</Button></Link>
        </div>
      </EmployerLayout>
    );
  }

  return (
    <EmployerLayout>
      <div className="max-w-xl space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">Company Profile</h1>
          {!editing ? (
            <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>Edit</Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => { setEditing(false); setForm(profile); }}>Cancel</Button>
              <Button size="sm" loading={saving} onClick={handleSave}>Save</Button>
            </div>
          )}
        </div>

        {/* Company Banner */}
        <div className="bg-gradient-to-r from-india to-emerald-600 rounded-2xl p-5 text-white">
          <h2 className="font-bold text-xl">{profile.company_name}</h2>
          <p className="text-green-100 text-sm mt-0.5 capitalize">{profile.employer_type}</p>
          <div className="mt-2">
            <Badge label={profile.verification_status} variant={profile.verification_status} />
          </div>
          {profile.verification_status !== 'verified' && (
            <p className="text-xs text-green-200 mt-2">
              Verification in progress — typically takes 1–2 business days
            </p>
          )}
        </div>

        {/* Security */}
        <SecuritySection />

        {/* Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h3 className="font-semibold text-gray-900 text-sm">Company Details</h3>
          {editing ? (
            <div className="space-y-3">
              <Input label="Company Name" value={form.company_name || ''} onChange={set('company_name')} />
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">About</label>
                <textarea rows={3} value={form.description || ''} onChange={set('description')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none resize-none" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input label="Contact Person" value={form.contact_person || ''} onChange={set('contact_person')} />
                <Input label="Designation" value={form.designation || ''} onChange={set('designation')} />
              </div>
              <Input label="Website" value={form.website || ''} onChange={set('website')} placeholder="https://example.com" />
              <Input label="Address" value={form.address_line || ''} onChange={set('address_line')} />
              <Input label="Pincode" value={form.pincode || ''} onChange={set('pincode')} />
            </div>
          ) : (
            <>
              {profile.description && (
                <p className="text-sm text-gray-600">{profile.description}</p>
              )}
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                {[
                  ['Contact', profile.contact_person],
                  ['Designation', profile.designation],
                  ['Website', profile.website],
                  ['GSTIN', profile.gstin],
                  ['PAN', profile.pan ? '••••' + profile.pan.slice(-4) : null],
                  ['Address', profile.address_line],
                  ['District', profile.district],
                  ['Pincode', profile.pincode],
                ].map(([label, val]) => (
                  <div key={label}>
                    <dt className="text-gray-400 text-xs">{label}</dt>
                    <dd className="font-medium text-gray-900 mt-0.5 text-sm">{val || '—'}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </div>
      </div>
    </EmployerLayout>
  );
}
